import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiGet } from '@/lib/api';
import '@/styles/login.css';

const ROLES = [
  { id: 'admin', label: 'Admin' },
  { id: 'karaikudi', label: 'Karaikudi' },
  { id: 'sivagangai', label: 'Sivagangai' },
];

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
);
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01" /></svg>
);
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
);

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [names, setNames] = useState([]);
  const [userId, setUserId] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pinInputRef = useRef(null);

  useEffect(() => {
    if (!role) return undefined;
    let active = true;
    apiGet(`/users/by-role?role=${role}`)
      .then((n) => { if (active) setNames(n); })
      .catch((e) => { console.warn('Load names failed:', e?.message || e); if (active) setNames([]); });
    return () => { active = false; };
  }, [role]);

  const selectRole = (r) => {
    setRole(r);
    setNames([]);
    setUserId('');
    setPin('');
    setError('');
  };

  const doLogin = async (uid, p) => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await login(uid, p);
      navigate('/dashboard');
    } catch (e) {
      setError(e.message || 'Wrong PIN');
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  const onPinChange = (e) => {
    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setPin(v);
    setError('');
    if (v.length === 6 && userId) {
      setTimeout(() => doLogin(userId, v), 250);
    }
  };

  const canSubmit = role && userId && pin.length === 6 && !submitting;

  return (
    <div className="login-page" data-testid="login-page">
      <div className="card">
        <div className="logo-wrap">
          <img src="/logo.png" alt="Infinity Planners" />
        </div>

        <div className="field-label">SELECT ROLE</div>
        <div className="group-row">
          {ROLES.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`group-tab${role === r.id ? ' selected' : ''}`}
              onClick={() => selectRole(r.id)}
              data-testid={`role-${r.id}`}
            >
              {r.id === 'admin' ? <ShieldIcon /> : <BuildingIcon />}
              <span className="group-tab-label">{r.label}</span>
            </button>
          ))}
        </div>

        <div className="field-label">SELECT NAME</div>
        <select
          className="name-select"
          value={userId}
          disabled={!role || names.length === 0}
          onChange={(e) => { setUserId(e.target.value); setPin(''); setError(''); }}
          data-testid="name-select"
        >
          <option value="">{!role ? 'Select a role first' : names.length === 0 ? 'Loading…' : 'Select name'}</option>
          {names.map((n) => (
            <option key={n.user_id} value={n.user_id}>{n.name}</option>
          ))}        </select>

        <div className="field-label">ENTER 6-DIGIT PIN</div>
        <div className={`pin-row${!userId ? ' disabled' : ''}`} onClick={() => pinInputRef.current && pinInputRef.current.focus()}>
          <div className="pin-boxes">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`pin-box${pin[i] ? ' filled' : ''}`} data-testid={`pin-box-${i}`}>
                {pin[i] ? '\u2022' : ''}
              </div>
            ))}
          </div>
          <input
            ref={pinInputRef}
            className="pin-hidden-input"
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={pin}
            disabled={!userId}
            onChange={onPinChange}
            autoComplete="one-time-code"
            data-testid="pin-input"
          />
        </div>

        <button
          type="button"
          className="signin-btn"
          disabled={!canSubmit}
          onClick={() => doLogin(userId, pin)}
          data-testid="signin-btn"
        >
          {submitting ? 'Signing in…' : 'Sign In'} <ArrowIcon />
        </button>

        {error && <div className="error-text" data-testid="login-error">{error}</div>}
        <div className="help-text">First-time login: enter any 6-digit PIN to set it.</div>
      </div>
    </div>
  );
}
